// Webhook types — the discriminated union over webhook event types, plus
// supporting shape declarations used by both the API surface and the
// internal delivery worker.

import type { Kysely } from "kysely";

/**
 * Alias for an explicit Kysely executor (db or transaction).
 */
export type DbExecutor = Kysely<any>;

/** Payload for `records.sync.completed`. Per-connection granularity (§3.9 Q2). */
export type RecordsSyncCompletedPayload = {
  /** Developer-provided patient identifier (`patient_org_membership.external_id`). */
  patient_id: string;
  /** Public id of the connected EHR (`fhir_source.public_id`, e.g. `fhirsrc_*`). */
  connection_id: string;
  /** Total FHIR retrievals attempted in this pull. */
  total: number;
  /** Subset of `total` that ended in `status='error'`. */
  errors: number;
  /** Wall-clock duration of the pull. `null` when `pull_completed_at` is not set. */
  duration_ms: number | null;
};

/** Payload for `records.sync.failed`. Free-text error only (§3.9 Q3). */
export type RecordsSyncFailedPayload = {
  patient_id: string;
  connection_id: string;
  error_message: string | null;
};

/**
 * Event-type discriminator. The four event types:
 *  - `patient_session.completed` — flow reaches `complete` or `expired`
 *  - `connection.token_refresh_failed` — final refresh failure
 *  - `records.sync.completed` / `records.sync.failed` — per pull_completion
 *
 * Endpoint auto-disable after retry exhaustion is *not* emitted as an event.
 * Inspect `status` via `GET /webhooks/{id}` to detect it.
 */
export const WEBHOOK_EVENT_TYPES = [
  "patient_session.completed",
  "connection.token_refresh_failed",
  "records.sync.completed",
  "records.sync.failed",
] as const;

export type WebhookEventType = (typeof WEBHOOK_EVENT_TYPES)[number];

/**
 * Endpoint lifecycle status. `active` accepts deliveries; `disabled` skips
 * them. Transition is via PATCH /webhooks/:id (Q10) or auto-disable after
 * retry exhaustion in the delivery worker.
 */
export type WebhookEndpointStatus = "active" | "disabled";

/**
 * Internal DB-row shape for `webhook_endpoint`. Timestamps are `Date`,
 * the encrypted secret + nonce are `Buffer`. Mapped to the public API
 * response shape via `toWebhookEndpointResponse` (drops the encrypted
 * fields). Don't return this directly from API handlers.
 */
export type WebhookEndpointRow = {
  id: string;
  public_id: string;
  organization_id: string;
  url: string;
  events: string[];
  secret_encrypted: Buffer;
  secret_nonce: Buffer;
  status: WebhookEndpointStatus;
  description: string | null;
  metadata: Record<string, unknown>;
  api_version: string;
  created_at: Date;
  updated_at: Date;
};

/**
 * Internal DB-row shape for `webhook_event`. Timestamps are `Date`,
 * payload is the stored JSONB. Mapped to the public API response shape
 * via `toWebhookEventResponse`.
 */
export type WebhookEventRow = {
  id: string;
  public_id: string;
  webhook_endpoint_id: string;
  type: WebhookEventType;
  payload: Record<string, unknown>;
  api_version: string;
  attempts: number;
  next_attempt_at: Date | null;
  delivered_at: Date | null;
  last_status_code: number | null;
  last_response_body: string | null;
  last_redelivered_at: Date | null;
  created_at: Date;
};

/**
 * Public response shape for a webhook endpoint. Mirrored to Zod via
 * `WebhookEndpointResponseSchema` in `src/server/schema/api/webhooks.ts`.
 * Never includes the plaintext signing secret — that's returned ONCE in
 * the create / rotate response envelopes only.
 */
export type WebhookEndpoint = {
  id: string;
  resource_type: "webhook_endpoint";
  url: string;
  events: WebhookEventType[] | ["*"];
  status: WebhookEndpointStatus;
  description: string | null;
  metadata: Record<string, unknown>;
  api_version: string;
  created_at: string;
  updated_at: string;
};

/**
 * Public response shape for a single webhook event (delivery record).
 * `data.object` is typed loosely as `unknown` here because the runtime
 * payload is one of four discriminated shapes — see {@link WebhookEvent}
 * for the typed union the SDK exposes.
 */
export type WebhookEventRecord = {
  id: string;
  resource_type: "event";
  webhook_endpoint_id: string;
  type: WebhookEventType;
  data: { object: unknown };
  api_version: string;
  attempts: number;
  next_attempt_at: string | null;
  delivered_at: string | null;
  last_status_code: number | null;
  last_response_body: string | null;
  last_redelivered_at: string | null;
  created_at: string;
};

/**
 * Discriminated union of every event the SDK can receive — keyed on `type`
 * so consumers can `switch (event.type)` and have TS narrow `data.object`.
 *
 * The PatientSession and Connection shapes for the first two events are imported
 * from the existing API types (not redefined here) so they stay in lockstep
 * with `toPatientSessionResponse` and `toConnectionResponse`.
 */
export type WebhookEvent =
  | WebhookEventEnvelope<"patient_session.completed", PatientSessionEventObject>
  | WebhookEventEnvelope<"connection.token_refresh_failed", ConnectionEventObject>
  | WebhookEventEnvelope<"records.sync.completed", RecordsSyncCompletedPayload>
  | WebhookEventEnvelope<"records.sync.failed", RecordsSyncFailedPayload>;

type WebhookEventEnvelope<T extends WebhookEventType, O> = {
  id: string;
  object: "event";
  type: T;
  data: { object: O };
  api_version: string;
  created_at: string;
};

/**
 * Loose alias for the PatientSession shape `toPatientSessionResponse` returns. We can't
 * import the inferred zod type without creating a circular dependency
 * (schema/api/patient-sessions imports from common which is also imported here);
 * downstream consumers of `WebhookEvent` get the same runtime shape and
 * the SDK re-exports the strongly-typed `PatientSession` from its own types.
 */
export type PatientSessionEventObject = {
  id: string;
  resource_type: "patient_session";
  status: "open" | "complete" | "expired";
  url: string;
  patient_id: string;
  connection_id: string | null;
  recommended_connection_ids: string[] | null;
  return_url: string;
  return_button_label: string | null;
  connections: Array<{
    id: string | null;
    connection_id: string;
    status: "active" | "failed" | "expired" | "refresh_failed" | "disconnected";
    failure_code: string | null;
    failure_at: string | null;
    created_at: string;
  }>;
  expires_at: string;
  metadata: Record<string, unknown>;
  created_at: string;
};

/**
 * Loose alias for the Connection shape `toConnectionResponse` returns.
 * Same circular-dep avoidance reasoning as `PatientSessionEventObject` above.
 */
export type ConnectionEventObject = {
  id: string;
  resource_type: "connection";
  patient_session_id: string | null;
  connection_id: string;
  status: "active" | "failed" | "expired" | "refresh_failed" | "disconnected";
  scopes: string[];
  failure_code: string | null;
  failure_at: string | null;
  created_at: string;
  updated_at: string;
};

/**
 * Internal shape used by the delivery worker — a single "due" `webhook_event`
 * joined with the endpoint columns needed to POST and verify.
 */
export type DueDelivery = {
  event_id: string;
  event_public_id: string;
  event_type: WebhookEventType;
  payload: { object: unknown };
  api_version: string;
  attempts: number;
  created_at: Date;
  endpoint_id: string;
  endpoint_public_id: string;
  endpoint_organization_id: string;
  url: string;
  secret_encrypted: Buffer;
  secret_nonce: Buffer;
};

/** Optional narrowing for one delivery tick — used by tests to claim only their own endpoint's rows. */
export type DeliveryTickScope = {
  endpointId?: string;
};

/** Typed service error raised when manual redelivery is still cooling down. */
export class WebhookRedeliveryThrottledError extends Error {
  readonly code = "redelivery_throttled";

  /** Creates a cooldown error with the remaining delay in whole seconds. */
  constructor(readonly retryAfter: number) {
    super(`Wait ${retryAfter} seconds before redelivering again`);
    this.name = "WebhookRedeliveryThrottledError";
  }
}
